import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputBase,
  IconButton,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DescriptionIcon from "@mui/icons-material/Description";
import LinkIcon from "@mui/icons-material/Link";
import FolderIcon from "@mui/icons-material/Folder";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import DoDisturbOnOutlinedIcon from "@mui/icons-material/DoDisturbOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Card from "../../common/Card";
import { tokens } from "../../../theme";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  DocumentFile,
  useDocumentReducer,
} from "../../../redux/slice/document/DocumentSlice";
import {
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../../redux/slice/codetableSlice";
import {
  fetchAppConfigList,
  fetchDocumentByIdCommonApiPublic,
  setSingleAlertObj,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import { EXTERNAL_LINK_TYPE } from "../../../constants/applicationConstants";
import { downloadBase64File } from "../../../utils/Utility";
import PopupSearch from "./PopupSearch";
import HaveAQuestionPopup from "./HaveAQuestionPopup";
import { NotListedLocationOutlined } from "@mui/icons-material";
import { useBranding } from "../../../BrandingContext";

const ExternalLink: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useAppDispatch();
  const branding = useBranding();

  const { documentCategoryList } = useDocumentReducer();
  const { documentDataFromCommonApiPublic, appConfigList } = useCommonReducer();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();

  const [productDocumentTypeMap, setProductDocumentTypeMap] = useState<
    Record<string, string>
  >({});
  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    documentName: string;
  }>({
    isShow: false,
    documentName: "",
  });
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(
    new Set()
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [haveAQuestionOpen, setHaveAQuestionOpen] = useState(false);

  const toggleAccordion = (key: string) => {
    setExpandedAccordions((prev) => {
      const copy = new Set(prev);
      copy.has(key) ? copy.delete(key) : copy.add(key);
      return copy;
    });
  };

  useEffect(() => {
    dispatch(fetchAppConfigList());
  }, []);

  

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      const sortedDocumentList = [
        ...getCodeTableValuesByTableName(
          allCodeTablesValuesMap,
          "ProductDocument"
        ),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      const map = Object.fromEntries(
        sortedDocumentList.map((item) => [item.keyName, item.displayValue])
      );
      setProductDocumentTypeMap(map);
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  const handleDocClick = (file: DocumentFile) => {
    if (
      file.externalLinkType === EXTERNAL_LINK_TYPE.EXTERNAL_URL ||
      (file.externalLinkType === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT &&
        !file.documentId)
    ) {
      window.open(file.productDocumentInfo?.documentURL, "_blank");
    } else {
      dispatch(fetchDocumentByIdCommonApiPublic(file.documentId)).then(
        (res) => {
          const doc = res.payload;
          if (doc?.content) {
            downloadBase64File(doc.content, doc.documentName, doc.fileType);
          } else {
            dispatch(
              setSingleAlertObj({
                message: "Document not found!",
                alertType: "info",
              })
            );
          }
        }
      );
    }
  };

  const renderFileList = (files: DocumentFile[]) =>
    files.map((file, idx) => (
      <Box
        key={`file-${idx}`}
        onClick={() => handleDocClick(file)}
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: theme.palette.mode === "dark"? "#3e3c4a" : "#d2d7d9",
          p: 1,
          borderRadius: 2,
          mb: 1,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: colors.grey[700],
            boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
          },
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            backgroundColor:
              file.externalLinkType === EXTERNAL_LINK_TYPE.EXTERNAL_URL ||
              (file.externalLinkType === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT &&
                !file.documentId)
                ? colors.info[400]
                : colors.success[400],
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
          }}
        >
          {file.externalLinkType === EXTERNAL_LINK_TYPE.EXTERNAL_URL ||
          (file.externalLinkType === EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT &&
            !file.documentId) ? (
            <LinkIcon />
          ) : (
            <DescriptionIcon />
          )}
        </Box>
        <Box>
          <Typography fontSize="14px">
            {file.documentName}
          </Typography>
          {file.externalLinkType !== EXTERNAL_LINK_TYPE.EXTERNAL_URL && (
            <Typography fontSize="14px" color={colors.grey[400]}>
              File type: {productDocumentTypeMap[file.documentType] || ""}
            </Typography>
          )}
        </Box>
      </Box>
    ));

  const [popupSearchOpen, setPopupSearchOpen] = useState(false);
  const [popupSearchTerm, setPopupSearchTerm] = useState("");

  function renderOtherFiles(category: { category?: string; subCategories: any; }) {
    const otherSubCategory = category.subCategories.find(
      (      subCategory: { subCategory: string; }) => subCategory.subCategory === "Other"
    );
    
    if (!otherSubCategory || otherSubCategory.files.length === 0) {
      return null;
    }
    
    return (
      <Box>
        {renderFileList(otherSubCategory.files)}
      </Box>
    );
  }

  useEffect(() => {
    if (appConfigList.length > 0) {
     
      const configItem = appConfigList.find(
        (item) => item.configName === "product.document.category.sequence"
      );
  
      if (configItem) {
        try {
          const configValue = JSON.parse(configItem.configValue);
          const expandCategoryList = configValue.expandCategory || {};

          const expandedSet = new Set<string>();
          documentCategoryList?.forEach((category, catIdx) => {
            if (expandCategoryList[category.category]) {
              expandedSet.add(`main-${catIdx}`);
            }
          });
          setExpandedAccordions(expandedSet);

        } catch (error) {
          console.error("Invalid JSON in product.document.category.sequence", error);
        }
      }
    }
  }, [appConfigList, documentCategoryList]);
  

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        backgroundColor: colors.grey[900],
        py: 6,
        px: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 1000, borderRadius: 3 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <img
            alt="V3 Biomed Logo"
            src={theme.palette.mode === "dark" ? branding?.logoWhite : branding?.logo || require("../../../branding-assets/v3biomed/logo.png")}
            style={{
              height: "80px",
              objectFit: "contain",
              maxWidth: "100%",
            }}
          />
          <Box sx={{ height: 12 }} />
          <Box
            onClick={() => setHaveAQuestionOpen(true)}
            sx={{
              px: 2,
              py: 1,
              backgroundColor: colors.primary[500],
              borderRadius: 2,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              transition: "all 0.3s ease",
              border:1,
              borderColor: colors.primary[400],
              "&:hover": {
                backgroundColor: colors.grey[700],
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                transform: "translateY(-2px)",
              },
            }}
          >
            <NotListedLocationOutlined sx={{ fontSize: 20, color: colors.grey[100] }} />
            <Typography fontWeight="bold" fontSize="15px" color={colors.grey[100]}>
              Have a question? Text/Call us
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              backgroundColor: colors.grey[700],
              borderRadius: 3,
              px: 1,
              py: 1,
              boxShadow: `0 2px 8px ${colors.grey[900]}`,
              "&:hover": {
                backgroundColor: colors.grey[600],
              },
            }}
          >
            <SearchIcon sx={{ color: colors.grey[400], mr: 1 }} />
            <InputBase
              fullWidth
              placeholder="Search documents..."
              value={searchTerm}
              onClick={() => setPopupSearchOpen(true)} // <-- ADD THIS
              onChange={(e) => {
                const term = e.target.value;
                setSearchTerm(term);
                const expandedSet = new Set<string>();
                if (term) {
                  documentCategoryList?.forEach((cat, i) => {
                    cat.subCategories.forEach((sub, j) => {
                      const match = sub.files.some((file) =>
                        file.documentName
                          ?.toLowerCase()
                          .includes(term.toLowerCase())
                      );
                      if (match) {
                        expandedSet.add(`main-${i}`);
                        expandedSet.add(`sub-${i}-${j}`);
                      }
                    });
                  });
                }
                setExpandedAccordions(expandedSet);
              }}
              sx={{
                flex: 1,
                fontSize: "16px",
                color: colors.grey[100],
                "&::placeholder": {
                  color: colors.grey[500],
                  opacity: 1,
                },
              }}
            />
          </Box>

          {searchTerm === "" && (
            <IconButton
              sx={{ ml: 2 }}
              onClick={() => {
                if (expandedAccordions.size > 0) {
                  setExpandedAccordions(new Set());
                } else {
                  const allKeys = new Set<string>();
                  documentCategoryList?.forEach((cat, i) => {
                    allKeys.add(`main-${i}`);
                    cat.subCategories.forEach((_, j) => {
                      allKeys.add(`sub-${i}-${j}`);
                    });
                  });
                  setExpandedAccordions(allKeys);
                }
              }}
            >
              {expandedAccordions.size > 0 ? (
                <DoDisturbOnOutlinedIcon />
              ) : (
                <AddCircleOutlineOutlinedIcon />
              )}
            </IconButton>
          )}
        </Box>

        {documentCategoryList?.map((category, catIdx) => {
          const matchingSubCategories = category.subCategories
            .map((subCategory, subIdx) => {
              const filteredFiles = subCategory.files.filter(
                (file: DocumentFile) =>
                  file.documentName
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase())
              );
              if (filteredFiles.length > 0) {
                return {
                  ...subCategory,
                  files: filteredFiles,
                  originalIndex: subIdx,
                };
              }
              return null;
            })
            .filter(Boolean);

          if (searchTerm && matchingSubCategories.length === 0) return null;

          return (
            <Accordion
              key={catIdx}
              expanded={expandedAccordions.has(`main-${catIdx}`)}
              onChange={() => toggleAccordion(`main-${catIdx}`)}
              sx={{
                backgroundColor: "transparent",
                boxShadow: "none",
                mb: 2,
                borderRadius: 2,
                border: 1,
                borderColor: colors.grey[600],
                ":hover": {
                  backgroundColor: theme.palette.mode === "dark"? "#0f0e16" : "#f7fdff",
                  borderColor: theme.palette.mode === "dark"? "#2d2a42" :"#7d7d7d",
                },
                "&::before": {
                  display: "none",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  paddingTop: 1,
                  paddingBottom: 1,
                  minHeight: 0.2,
                  "&.Mui-expanded": { minHeight: 0.2 },
                  "& .MuiAccordionSummary-content": {
                    margin: 0.3,
                    padding: 0.3,
                    minHeight: 0.3,
                  },
                  "& .MuiAccordionSummary-content.Mui-expanded": {
                    margin: 0.3,
                    padding: 0.3,
                    minHeight: 0.3,
                  },
                }}
              >
                <Typography fontWeight="bold" fontSize="17px">
                  {category.category}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {matchingSubCategories
                .filter(subCategory => subCategory && subCategory.subCategory !== "Other")
                .map((subCategory: any) => (
                  <Accordion
                    key={`subcategory-${subCategory.originalIndex}`}
                    expanded={expandedAccordions.has(
                      `sub-${catIdx}-${subCategory.originalIndex}`
                    )}
                    onChange={() =>
                      toggleAccordion(
                        `sub-${catIdx}-${subCategory.originalIndex}`
                      )
                    }
                    sx={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      mb: 1,
                      borderRadius: 2,
                      ":hover": {
                        backgroundColor: theme.palette.mode === "dark"? "#262530" : "#e8eced",
                      },
                      "&::before": {
                        display: "none",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        minHeight: 0.2,
                        "&.Mui-expanded": { minHeight: 0.2 },
                        "& .MuiAccordionSummary-content": {
                          margin: 0.3,
                          padding: 0.3,
                          minHeight: 0.3,
                        },
                        "& .MuiAccordionSummary-content.Mui-expanded": {
                          margin: 0.3,
                          padding: 0.3,
                          minHeight: 0.3,
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FolderIcon
                          sx={{ fontSize: 28, color: "#4A90E2", mr: 1 }}
                        />
                        <Typography fontWeight="bold" fontSize="15px">
                          {subCategory.subCategory}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {renderFileList(subCategory.files)}
                    </AccordionDetails>
                  </Accordion>
                ))}
                {renderOtherFiles(category)}
              </AccordionDetails>
            </Accordion>
          );
        })}

        {showPDF.isShow && (
          <PDFViewer
            isOpen={showPDF.isShow}
            onClose={() => setShowPDF({ isShow: false, documentName: "" })}
            title={EXTERNAL_LINK_TYPE.PRODUCT_DOCUMENT}
            fileName={showPDF.documentName}
            base64String={documentDataFromCommonApiPublic?.content || ""}
          />
        )}
      </Card>

      <PopupSearch
        open={popupSearchOpen}
        onClose={() => setPopupSearchOpen(false)}
        onSearch={(value) => setPopupSearchTerm(value)}
        documentCategoryList={documentCategoryList}
        documentClick={handleDocClick}
      />

      <HaveAQuestionPopup
        open={haveAQuestionOpen}
        onClose={() => setHaveAQuestionOpen(false)}
      />

    </Box>
  );
};

export default ExternalLink;
